import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config';
import './globalforecasting.css';

// Map of AcctNum to channel and display name
const SALES_ACCOUNTS = [
  { acctNum: '4100', channel: 'shopify', name: 'Shopify Sales' },
  { acctNum: '4200', channel: 'amazon', name: 'Amazon Sales' },
  { acctNum: '4500', channel: 'trade', name: 'Trade Sales' },
  { acctNum: '4205', channel: 'amazon', name: 'Amazon Promotional Rebates' },
  { acctNum: '4700', channel: 'amazon', name: 'Amazon Discounts & Returns' },
];

// Cost accounts (separate from main sales accounts)
const COST_ACCOUNTS = [
  { acctNum: '5100', channel: 'cost', name: 'Cost of Goods - Base Materials' },
  { acctNum: '5200', channel: 'cost', name: 'Cost of Goods - Packaging' },
];

// Operating expense accounts in specified order
const OPERATING_EXPENSE_ACCOUNTS = [
  { acctNum: '5400', name: 'Product Research & Development' },
  { acctNum: '6100', name: 'Amazon Advertising Fees' },
  { acctNum: '6200', name: 'Amazon FBA Fees' },
  { acctNum: '6400', name: 'Amazon Selling Fees' },
  { acctNum: '6500', name: 'Amazon Subscription Fees' },
  { acctNum: '6600', name: 'Shipping Cost' },
  { acctNum: '6602', name: 'Storage - Warehouse' },
  { acctNum: '6700', name: 'Payment Processing Fees' },
  { acctNum: '7200', name: 'Financial Services' },
  { acctNum: '7300', name: 'Marketing Services' },
  { acctNum: '7400', name: 'Legal Services' },
  { acctNum: '7510', name: 'Payroll Wage Expenses' },
  { acctNum: '7520', name: 'Payroll Tax Expenses' },
  { acctNum: '7800', name: 'Gifts' },
  { acctNum: '8103', name: 'Rent' },
  { acctNum: '8105', name: 'Insurance' },
  { acctNum: '8110', name: 'Telephone/Internet Expense' },
  { acctNum: '8120', name: 'Office Supplies' },
  { acctNum: '8122', name: 'Continuing Education' },
  { acctNum: '8250', name: 'Dues & Subscriptions' },
  { acctNum: '8340', name: 'Bank Charges & Fees' },
  { acctNum: '8350', name: 'Miscellaneous Expense' },
  { acctNum: '8403', name: 'Job Supplies - Labels' },
  { acctNum: '8407', name: 'Job Supplies - Print Plates' },
  { acctNum: '8409', name: 'Job Supplies - Cutting Dies' },
  { acctNum: '8500', name: 'Software Services' },
  { acctNum: '8510', name: 'Marketing Software' },
  { acctNum: '8520', name: 'Technical Software' },
  { acctNum: '8530', name: 'Software Development' },
  { acctNum: '8700', name: 'Travel & Meals' },
  { acctNum: '8800', name: 'Marketing' },
  { acctNum: '8830', name: 'Donations & Samples' },
  { acctNum: '8355', name: 'Conferences & Expos' },
  { acctNum: '8890', name: 'Advertising Costs' },
  { acctNum: '8600', name: 'Taxes & Licenses' },
];

// Balance sheet forecast accounts
const BALANCE_SHEET_ACCOUNTS = [
  { acctNum: '1410', name: 'Raw Material Inventory' },
  { acctNum: '1420', name: 'Finished Goods Inventory' },
  { acctNum: '1500', name: 'Accounts Receivable' },
  { acctNum: '2100', name: 'Accounts Payable' },
  { acctNum: '2240', name: 'Credit Cards Payable' },
  { acctNum: '9000', name: 'Total Working Capital' },
];

// Helper to get account name from financial_forecast_approach table
function getAccountName(acctNum, accountNames) {
  return accountNames[acctNum] || acctNum;
}

// Helper to check if account is a product sale (for subtotal grouping)
function isProductSale(acctNum) {
  return ['4100', '4200', '4500'].includes(acctNum);
}

// Helper to check if account is a revenue adjustment (for total revenue calculation)
function isRevenueAdjustment(acctNum) {
  return ['4205', '4700'].includes(acctNum);
}

// Helper to check if account is a cost of goods (for cost calculations)
function isCostOfGoods(acctNum) {
  return ['5100', '5200'].includes(acctNum);
}

// Helper to check if account is an operating expense
function isOperatingExpense(acctNum) {
  return OPERATING_EXPENSE_ACCOUNTS.some(account => account.acctNum === acctNum);
}

// Debug helper function for better console formatting
const debugLog = (label, data, type = 'log') => {
  console.group(`🔍 ${label}`);
  if (type === 'table' && Array.isArray(data)) {
    console.table(data);
  } else if (type === 'object') {
    console.log('Data:', JSON.stringify(data, null, 2));
  } else {
    console.log(data);
  }
  console.groupEnd();
};

const PLforecast = () => {
  const [scenarios, setScenarios] = useState([]);
  const [selectedScenario, setSelectedScenario] = useState('Momentum Forecast');
  const [loading, setLoading] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [error, setError] = useState('');
  const [salesData, setSalesData] = useState({}); // { acctNum: { month: value, ... }, ... }
  const [accountNames, setAccountNames] = useState({}); // { acctNum: name }
  const [forecastMethods, setForecastMethods] = useState({}); // { acctNum: method }
  const [editableValues, setEditableValues] = useState({}); // { acctNum: { month: value, ... }, ... }
  const [balanceSheetData, setBalanceSheetData] = useState({}); // { acctNum: { month: value, ... }, ... }

  // Debug state changes
  useEffect(() => {
    debugLog('State Update - Scenarios', scenarios, 'table');
  }, [scenarios]);

  useEffect(() => {
    debugLog('State Update - Selected Scenario', selectedScenario);
  }, [selectedScenario]);

  useEffect(() => {
    debugLog('State Update - Loading', loading);
  }, [loading]);

  useEffect(() => {
    debugLog('State Update - Error', error);
  }, [error]);

  useEffect(() => {
    debugLog('State Update - Sales Data', salesData, 'object');
  }, [salesData]);

  useEffect(() => {
    debugLog('State Update - Account Names', accountNames, 'object');
  }, [accountNames]);

  useEffect(() => {
    debugLog('State Update - Forecast Methods', forecastMethods, 'object');
  }, [forecastMethods]);

  useEffect(() => {
    debugLog('State Update - Editable Values', editableValues, 'object');
  }, [editableValues]);

  // Fetch scenarios for selector
  useEffect(() => {
    const fetchScenarios = async () => {
      debugLog('Fetching Scenarios', 'Starting API call...');
      try {
        const response = await axios.get(`${API_BASE_URL}/api/financial-forecast/scenarios`);
        debugLog('Scenarios API Response', response.data);
        setScenarios(response.data.scenarios || []);
      } catch (err) {
        debugLog('Scenarios API Error', {
          message: err.message,
          status: err.response?.status,
          data: err.response?.data
        }, 'object');
        setError('Failed to load scenarios');
      }
    };
    fetchScenarios();
  }, []);

  // Fetch account names from financial_forecast_approach table
  useEffect(() => {
    const fetchAccountNames = async () => {
      debugLog('Fetching Account Names', 'Starting API call...');
      try {
        const response = await axios.get(`${API_BASE_URL}/api/global-forecast/account-names`);
        debugLog('Account Names API Response', response.data);
        setAccountNames(response.data || {});
      } catch (err) {
        debugLog('Account Names API Error', {
          message: err.message,
          status: err.response?.status,
          data: err.response?.data
        }, 'object');
        setError('Failed to load account names');
      }
    };
    fetchAccountNames();
  }, []);

  // Fetch forecast methods from financial_forecast_approach table
  useEffect(() => {
    const fetchForecastMethods = async () => {
      debugLog('Fetching Forecast Methods', 'Starting API call...');
      try {
        const response = await axios.get(`${API_BASE_URL}/api/financial-forecast/methods`);
        debugLog('Forecast Methods API Response', response.data);
        setForecastMethods(response.data || {});
        
        // Debug: Log which accounts are User-input
        const userInputAccounts = Object.entries(response.data || {}).filter(([acctNum, method]) => method === 'User-input');
        debugLog('User-input Accounts Found', userInputAccounts);
      } catch (err) {
        debugLog('Forecast Methods API Error', {
          message: err.message,
          status: err.response?.status,
          data: err.response?.data
        }, 'object');
        setError('Failed to load forecast methods');
      }
    };
    fetchForecastMethods();
  }, []);

  // Fetch financial forecast data for selected scenario
  useEffect(() => {
    const fetchFinancialData = async () => {
      debugLog('Fetching Financial Data', `Selected scenario: ${selectedScenario}`);
      setLoading(true);
      setError('');
      try {
        const response = await axios.get(`${API_BASE_URL}/api/financial-forecast/data`, {
          params: { scenario_name: selectedScenario }
        });
        debugLog('Financial Data API Response', response.data);
        
        // Debug specific accounts and months
        if (response.data['4200']) {
          const amazonData = response.data['4200'];
          debugLog('Amazon Sales Raw Data', amazonData);
          
          const septData = amazonData.find(m => m.month === '2025-09');
          const novData = amazonData.find(m => m.month === '2025-11');
          debugLog('Amazon Sales Debug', {
            '2025-09': septData?.momentum_sales,
            '2025-11': novData?.momentum_sales,
            allMonths: amazonData.map(m => ({ month: m.month, value: m.momentum_sales }))
          });
        }
        
        // Debug November data for all accounts
        const novemberData = {};
        Object.keys(response.data).forEach(acctNum => {
          const novData = response.data[acctNum].find(m => m.month === '2025-11');
          if (novData) {
            novemberData[acctNum] = novData.momentum_sales;
          }
        });
        debugLog('November 2025 Data', novemberData);
        
        setSalesData(response.data);
        
        // Initialize editable values for User-input accounts
        const newEditableValues = {};
        Object.keys(response.data).forEach(acctNum => {
          if (forecastMethods[acctNum] === 'User-input') {
            newEditableValues[acctNum] = {};
            response.data[acctNum].forEach(monthData => {
              newEditableValues[acctNum][monthData.month] = monthData.momentum_sales;
            });
          }
        });
        setEditableValues(newEditableValues);
      } catch (err) {
        debugLog('Financial Data API Error', {
          message: err.message,
          status: err.response?.status,
          data: err.response?.data
        }, 'object');
        setError('Failed to load financial forecast data');
      } finally {
        setLoading(false);
      }
    };
    fetchFinancialData();
  }, [selectedScenario, forecastMethods]);

  // Fetch balance sheet forecast data for selected scenario
  useEffect(() => {
    const fetchBalanceSheetData = async () => {
      debugLog('Fetching Balance Sheet Data', `Selected scenario: ${selectedScenario}`);
      try {
        const response = await axios.get(`${API_BASE_URL}/api/balance-sheet-forecast/data`, {
          params: { scenario_name: selectedScenario }
        });
        debugLog('Balance Sheet Data API Response', response.data);
        setBalanceSheetData(response.data);
      } catch (err) {
        debugLog('Balance Sheet Data API Error', {
          message: err.message,
          status: err.response?.status,
          data: err.response?.data
        }, 'object');
        // Don't set error for balance sheet data as it's optional
        console.warn('Failed to load balance sheet data:', err.message);
      }
    };
    
    if (selectedScenario) {
      fetchBalanceSheetData();
    }
  }, [selectedScenario]);

  // Handler for scenario change
  const handleScenarioChange = (e) => {
    const newScenario = e.target.value;
    debugLog('Scenario Change', `From: "${selectedScenario}" To: "${newScenario}"`);
    setSelectedScenario(newScenario);
  };

  // Handler for recalculation
  const handleRecalculate = async () => {
    debugLog('Recalculate Button Clicked', 'Starting recalculation...');
    setRecalculating(true);
    setError('');
    try {
      const response = await axios.post(`${API_BASE_URL}/api/financial-forecast/recalculate`);
      debugLog('Recalculate API Response', response.data);
      
      if (response.data.success) {
        // Refresh the page to show updated data
        window.location.reload();
      } else {
        setError(response.data.message || 'Recalculation failed');
      }
    } catch (err) {
      debugLog('Recalculate API Error', {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data
      }, 'object');
      setError('Failed to recalculate financial forecasts');
    } finally {
      setRecalculating(false);
    }
  };

  // Handler for editable value changes
  const handleEditableValueChange = (acctNum, month, value) => {
    setEditableValues(prev => ({
      ...prev,
      [acctNum]: {
        ...prev[acctNum],
        [month]: parseFloat(value) || 0
      }
    }));
  };

  // Handler for save scenario
  const handleSaveScenario = async () => {
    debugLog('Save Scenario Button Clicked', 'Starting save...');
    setLoading(true);
    setError('');
    try {
      // Prepare the data to save
      const scenarioData = {
        scenario_name: selectedScenario,
        accounts: []
      };

      // Add all editable values to the scenario data
      Object.keys(editableValues).forEach(acctNum => {
        Object.keys(editableValues[acctNum]).forEach(month => {
          scenarioData.accounts.push({
            AcctNum: parseInt(acctNum),
            Month: month,
            amount: editableValues[acctNum][month]
          });
        });
      });

      const response = await axios.post(`${API_BASE_URL}/api/financial-forecast/save-scenario`, scenarioData);
      debugLog('Save Scenario API Response', response.data);
      
      if (response.data.success) {
        // Refresh the data to show the saved values
        const dataResponse = await axios.get(`${API_BASE_URL}/api/financial-forecast/data`, {
          params: { scenario_name: selectedScenario }
        });
        setSalesData(dataResponse.data);
        setError('');
      } else {
        setError(response.data.message || 'Save failed');
      }
    } catch (err) {
      debugLog('Save Scenario API Error', {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data
      }, 'object');
      setError('Failed to save scenario');
    } finally {
      setLoading(false);
    }
  };

  // Helper to get month display label
  const getMonthLabel = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  // Get all unique months from the data
  const getAllMonths = () => {
    const allMonths = new Set();
    Object.values(salesData).forEach(accountData => {
      if (Array.isArray(accountData)) {
        accountData.forEach(monthData => {
          allMonths.add(monthData.month);
        });
      }
    });
    const sortedMonths = Array.from(allMonths).sort();
    debugLog('Unique Months Found', sortedMonths);
    return sortedMonths;
  };

  const months = getAllMonths();

  // Helper to get value for a specific account and month
  const getAccountValue = (acctNum, month) => {
    const accountData = salesData[acctNum];
    if (!accountData) {
      debugLog(`No data found for account ${acctNum}`);
      return 0;
    }
    
    const monthData = accountData.find(m => m.month === month);
    const value = monthData ? monthData.momentum_sales : 0;
    
    // Debug specific accounts and months
    if (acctNum === '4200' && (month === '2025-09' || month === '2025-11')) {
      debugLog(`Getting Amazon Sales for ${month}`, {
        accountData: accountData,
        monthData: monthData,
        value: value,
        allMonths: accountData.map(m => ({ month: m.month, value: m.momentum_sales }))
      });
    }
    
    return value;
  };

  // Helper to get editable value for a specific account and month
  const getEditableValue = (acctNum, month) => {
    return editableValues[acctNum]?.[month] || 0;
  };

  // Helper to check if account is editable (User-input method)
  const isAccountEditable = (acctNum) => {
    const method = forecastMethods[acctNum];
    const isEditable = method === 'User-input';
    debugLog(`Checking if account ${acctNum} is editable`, {
      method,
      isEditable,
      allMethods: forecastMethods
    });
    return isEditable;
  };

  // Helper to get balance sheet value for a specific account and month
  const getBalanceSheetValue = (acctNum, month) => {
    const accountData = balanceSheetData[acctNum];
    if (!accountData) {
      return 0;
    }
    
    const monthData = accountData.find(m => m.month === month);
    return monthData ? monthData.amount : 0;
  };

  // Debug render data
  debugLog('Render Data', {
    scenariosCount: scenarios.length,
    selectedScenario,
    loading,
    recalculating,
    error,
    salesDataKeys: Object.keys(salesData),
    monthsCount: months.length,
    editableAccounts: Object.keys(editableValues)
  }, 'object');

  return (
    <div className="global-forecasting-container">
      <div className="scenario-management">
        <div className="scenario-controls">
          <div className="scenario-selector">
            <label htmlFor="scenario-select">Load Scenario:</label>
            <select
              id="scenario-select"
              value={selectedScenario}
              onChange={handleScenarioChange}
              disabled={loading || recalculating}
            >
              {scenarios.map((scenario) => (
                <option key={scenario} value={scenario}>{scenario}</option>
              ))}
            </select>
          </div>
          <button 
            className="recalculate-button" 
            onClick={handleRecalculate} 
            disabled={recalculating}
          >
            {recalculating ? 'Recalculating...' : 'Re-calculate Financial Forecasts'}
          </button>
          <button 
            className="save-button" 
            onClick={handleSaveScenario} 
            disabled={loading || Object.keys(editableValues).length === 0}
          >
            Save Scenario
          </button>
        </div>
      </div>
      {error && <div className="error-message">{error}</div>}
      <div className="tab-content">
        <h2>Profit &amp; Loss Forecast</h2>
        <div className="assumptions-table-container">
          <table className="assumptions-table">
            <thead>
              <tr>
                <th>Account</th>
                {months.map((month) => (
                  <th key={month}>{getMonthLabel(month)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Sales Accounts */}
              {SALES_ACCOUNTS.map((acct, index) => (
                <React.Fragment key={acct.acctNum}>
                  <tr>
                    <td>{getAccountName(acct.acctNum, accountNames)}</td>
                    {months.map((month) => {
                      const value = getAccountValue(acct.acctNum, month);
                      const displayValue = loading
                        ? '...'
                        : `$${parseFloat(value).toLocaleString()}`;
                      
                      return (
                        <td key={month} className="momentum-sales-cell">
                          {displayValue}
                        </td>
                      );
                    })}
                  </tr>
                  
                  {/* Add Product Sales subtotal after Trade Sales (4500) */}
                  {acct.acctNum === '4500' && (
                    <tr className="subtotal-row">
                      <td className="subtotal-label">Product Sales</td>
                      {months.map((month) => {
                        const productSalesTotal = SALES_ACCOUNTS
                          .filter(acct => isProductSale(acct.acctNum))
                          .reduce((total, acct) => {
                            return total + getAccountValue(acct.acctNum, month);
                          }, 0);
                        
                        const displayValue = loading
                          ? '...'
                          : `$${parseFloat(productSalesTotal).toLocaleString()}`;
                        
                        return (
                          <td key={month} className="subtotal-cell">
                            {displayValue}
                          </td>
                        );
                      })}
                    </tr>
                  )}
                </React.Fragment>
              ))}
              
              {/* Total Revenue */}
              <tr className="total-row">
                <td className="total-label">Total Revenue</td>
                {months.map((month) => {
                  const totalRevenue = SALES_ACCOUNTS
                    .reduce((total, acct) => {
                      return total + getAccountValue(acct.acctNum, month);
                    }, 0);
                  
                  const displayValue = loading
                    ? '...'
                    : `$${parseFloat(totalRevenue).toLocaleString()}`;
                  
                  return (
                    <td key={month} className="total-cell">
                      {displayValue}
                    </td>
                  );
                })}
              </tr>
              
              {/* Cost of Goods Sold */}
              {COST_ACCOUNTS.map((acct) => (
                <tr key={acct.acctNum}>
                  <td>{getAccountName(acct.acctNum, accountNames)}</td>
                  {months.map((month) => {
                    const value = getAccountValue(acct.acctNum, month);
                    const displayValue = loading
                      ? '...'
                      : `$${parseFloat(value).toLocaleString()}`;
                    
                    return (
                      <td key={month} className="momentum-sales-cell negative">
                        {displayValue}
                      </td>
                    );
                  })}
                </tr>
              ))}
              
              {/* Total Cost of Goods Sold */}
              <tr className="subtotal-row">
                <td className="subtotal-label">Total Cost of Goods Sold</td>
                {months.map((month) => {
                  const totalCostOfGoods = COST_ACCOUNTS
                    .reduce((total, acct) => {
                      return total + getAccountValue(acct.acctNum, month);
                    }, 0);
                  
                  const displayValue = loading
                    ? '...'
                    : `$${parseFloat(totalCostOfGoods).toLocaleString()}`;
                  
                  return (
                    <td key={month} className="subtotal-cell negative">
                      {displayValue}
                    </td>
                  );
                })}
              </tr>
              
              {/* Gross Profit */}
              <tr className="total-row">
                <td className="total-label">GROSS PROFIT</td>
                {months.map((month) => {
                  const totalRevenue = SALES_ACCOUNTS
                    .reduce((total, acct) => {
                      return total + getAccountValue(acct.acctNum, month);
                    }, 0);
                  
                  const totalCostOfGoods = COST_ACCOUNTS
                    .reduce((total, acct) => {
                      return total + getAccountValue(acct.acctNum, month);
                    }, 0);
                  
                  const grossProfit = totalRevenue - totalCostOfGoods;
                  const displayValue = loading
                    ? '...'
                    : `$${parseFloat(grossProfit).toLocaleString()}`;
                  
                  return (
                    <td key={month} className={`total-cell ${grossProfit >= 0 ? 'positive' : 'negative'}`}>
                      {displayValue}
                    </td>
                  );
                })}
              </tr>

              {/* Operating Expenses */}
              {OPERATING_EXPENSE_ACCOUNTS.map((acct) => {
                const isEditable = isAccountEditable(acct.acctNum);
                debugLog(`Rendering operating expense account ${acct.acctNum}`, {
                  accountName: getAccountName(acct.acctNum, accountNames),
                  isEditable,
                  forecastMethod: forecastMethods[acct.acctNum]
                });
                
                return (
                  <tr key={acct.acctNum}>
                    <td>{getAccountName(acct.acctNum, accountNames)}</td>
                    {months.map((month) => {
                      const value = isEditable ? getEditableValue(acct.acctNum, month) : getAccountValue(acct.acctNum, month);
                      
                      if (isEditable) {
                        return (
                          <td key={month} className="editable-cell">
                            <input
                              type="number"
                              value={value}
                              onChange={(e) => handleEditableValueChange(acct.acctNum, month, e.target.value)}
                              className="editable-input"
                              step="0.01"
                            />
                          </td>
                        );
                      } else {
                        const displayValue = loading
                          ? '...'
                          : `$${parseFloat(value).toLocaleString()}`;
                        
                        return (
                          <td key={month} className="momentum-sales-cell negative">
                            {displayValue}
                          </td>
                        );
                      }
                    })}
                  </tr>
                );
              })}

              {/* Total Operating Expenses */}
              <tr className="subtotal-row">
                <td className="subtotal-label">Total Operating Expenses</td>
                {months.map((month) => {
                  const totalOperatingExpenses = OPERATING_EXPENSE_ACCOUNTS
                    .reduce((total, acct) => {
                      const isEditable = isAccountEditable(acct.acctNum);
                      const value = isEditable ? getEditableValue(acct.acctNum, month) : getAccountValue(acct.acctNum, month);
                      return total + value;
                    }, 0);
                  
                  const displayValue = loading
                    ? '...'
                    : `$${parseFloat(totalOperatingExpenses).toLocaleString()}`;
                  
                  return (
                    <td key={month} className="subtotal-cell negative">
                      {displayValue}
                    </td>
                  );
                })}
              </tr>

              {/* Net Income */}
              <tr className="total-row">
                <td className="total-label">NET INCOME</td>
                {months.map((month) => {
                  const totalRevenue = SALES_ACCOUNTS
                    .reduce((total, acct) => {
                      return total + getAccountValue(acct.acctNum, month);
                    }, 0);
                  
                  const totalCostOfGoods = COST_ACCOUNTS
                    .reduce((total, acct) => {
                      return total + getAccountValue(acct.acctNum, month);
                    }, 0);
                  
                  const totalOperatingExpenses = OPERATING_EXPENSE_ACCOUNTS
                    .reduce((total, acct) => {
                      const isEditable = isAccountEditable(acct.acctNum);
                      const value = isEditable ? getEditableValue(acct.acctNum, month) : getAccountValue(acct.acctNum, month);
                      return total + value;
                    }, 0);
                  
                  const netIncome = totalRevenue - totalCostOfGoods - totalOperatingExpenses;
                  const displayValue = loading
                    ? '...'
                    : `$${parseFloat(netIncome).toLocaleString()}`;
                  
                  return (
                    <td key={month} className={`total-cell ${netIncome >= 0 ? 'positive' : 'negative'}`}>
                      {displayValue}
                    </td>
                  );
                })}
              </tr>

              {/* Balance Sheet Forecast Section */}
              <tr className="section-header">
                <td colSpan={months.length + 1} className="section-header-cell">
                  <strong>Balance Sheet Forecast</strong>
                </td>
              </tr>

              {/* Balance Sheet Accounts */}
              {BALANCE_SHEET_ACCOUNTS.slice(0, 5).map((acct) => (
                <tr key={acct.acctNum}>
                  <td>{acct.name}</td>
                  {months.map((month) => {
                    const value = getBalanceSheetValue(acct.acctNum, month);
                    const displayValue = loading
                      ? '...'
                      : `$${parseFloat(value).toLocaleString()}`;
                    
                    return (
                      <td key={month} className="balance-sheet-cell">
                        {displayValue}
                      </td>
                    );
                  })}
                </tr>
              ))}



              {/* Total Working Capital */}
              <tr className="total-row">
                <td className="total-label">Total Working Capital</td>
                {months.map((month) => {
                  const value = getBalanceSheetValue('9000', month);
                  const displayValue = loading
                    ? '...'
                    : `$${parseFloat(value).toLocaleString()}`;
                  
                  return (
                    <td key={month} className="total-cell">
                      {displayValue}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PLforecast; 